import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Button,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import { getFirestore, collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import {
  Star as StarIcon,
  RateReview as ReviewIcon,
  Visibility as ViewIcon,
  Chat as ChatIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Euro as EuroIcon
} from '@mui/icons-material';

interface Rental {
  id: string;
  dressId: string;
  dressName: string;
  dressImage: string;
  ownerName: string;
  ownerAvatar?: string;
  renterName?: string;
  renterAvatar?: string;
  startDate: string;
  endDate: string;
  totalPrice: number;
  status: 'completed' | 'active' | 'cancelled' | 'pending';
  pickupAddress: string;
  returnAddress: string;
  rating?: {
    fromRenter?: number;
    fromOwner?: number;
  };
  reviews?: {
    fromRenter?: {
      rating: number;
      comment: string;
      date: string;
    };
    fromOwner?: {
      rating: number;
      comment: string;
      date: string;
    };
  };
}

interface RentalHistoryProps {
  userId: string;
  userType: 'renter' | 'owner';
  title?: string;
}

const RentalHistory: React.FC<RentalHistoryProps> = ({ userId, userType, title }) => {
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRental, setSelectedRental] = useState<Rental | null>(null);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewData, setReviewData] = useState({
    rating: 0,
    comment: ''
  });

  useEffect(() => {
    loadRentalHistory();
  }, [userId, userType]);

  const loadRentalHistory = async () => {
    try {
      // Charger les vraies données depuis Firestore
      const db = getFirestore();
      let rentalsQuery;
      
      if (userType === 'renter') {
        // Locations que l'utilisateur a louées
        rentalsQuery = query(
          collection(db, 'bookings'),
          where('renterId', '==', userId)
        );
      } else {
        // Robes de l'utilisateur qui ont été louées
        rentalsQuery = query(
          collection(db, 'bookings'),
          where('ownerId', '==', userId)
        );
      }
      
      const bookingsSnapshot = await getDocs(rentalsQuery);
      const rentalsData: Rental[] = [];
      
      for (const bookingDoc of bookingsSnapshot.docs) {
        const bookingData = bookingDoc.data();
        
        // Charger les infos de la robe
        const dressDoc = await getDoc(doc(db, 'dresses', bookingData.dressId));
        const dressData = dressDoc.exists() ? dressDoc.data() : null;
        
        // Charger les infos du propriétaire/locataire
        const otherUserId = userType === 'renter' ? bookingData.ownerId : bookingData.renterId;
        const otherUserDoc = await getDoc(doc(db, 'users', otherUserId));
        const otherUserData = otherUserDoc.exists() ? otherUserDoc.data() : null;
        
        rentalsData.push({
          id: bookingDoc.id,
          dressId: bookingData.dressId,
          dressName: dressData?.title || dressData?.name || 'Robe',
          dressImage: dressData?.images?.[0] || dressData?.image || '',
          ownerName: userType === 'renter' 
            ? (otherUserData?.displayName || otherUserData?.name || 'Propriétaire')
            : 'Vous',
          ownerAvatar: userType === 'renter' 
            ? (otherUserData?.photoURL || otherUserData?.avatar || '')
            : '',
          renterName: userType === 'owner' 
            ? (otherUserData?.displayName || otherUserData?.name || 'Locataire')
            : undefined,
          renterAvatar: userType === 'owner' 
            ? (otherUserData?.photoURL || otherUserData?.avatar || '')
            : undefined,
          startDate: bookingData.startDate || bookingData.start,
          endDate: bookingData.endDate || bookingData.end,
          totalPrice: bookingData.totalPrice || bookingData.price || 0,
          status: bookingData.status || 'pending',
          pickupAddress: bookingData.pickupAddress || '',
          returnAddress: bookingData.returnAddress || '',
          rating: bookingData.rating || undefined,
          reviews: bookingData.reviews || undefined
        });
      }
      
      setRentals(rentalsData);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
      setRentals([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      completed: { label: 'Terminée', color: 'success' as const },
      active: { label: 'En cours', color: 'primary' as const },
      cancelled: { label: 'Annulée', color: 'error' as const },
      pending: { label: 'En attente', color: 'warning' as const }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  const handleReviewSubmit = async () => {
    if (!selectedRental || reviewData.rating === 0) return;

    try {
      // Simulation de soumission d'avis - à remplacer par un appel API réel
      console.log('Soumission avis:', {
        rentalId: selectedRental.id,
        rating: reviewData.rating,
        comment: reviewData.comment,
        userType
      });

      // Mise à jour locale
      setRentals(prev => prev.map(rental => 
        rental.id === selectedRental.id 
          ? {
              ...rental,
              rating: {
                fromRenter: rental.rating?.fromRenter,
                fromOwner: rental.rating?.fromOwner,
                [userType === 'renter' ? 'fromRenter' : 'fromOwner']: reviewData.rating
              },
              reviews: {
                fromRenter: rental.reviews?.fromRenter,
                fromOwner: rental.reviews?.fromOwner,
                [userType === 'renter' ? 'fromRenter' : 'fromOwner']: {
                  rating: reviewData.rating,
                  comment: reviewData.comment,
                  date: new Date().toISOString().split('T')[0]
                }
              }
            }
          : rental
      ));

      setReviewDialog(false);
      setReviewData({ rating: 0, comment: '' });
      setSelectedRental(null);
    } catch (error) {
      console.error('Erreur lors de la soumission de l\'avis:', error);
    }
  };

  const canReview = (rental: Rental) => {
    return rental.status === 'completed' && 
           (!rental.reviews || !rental.reviews[userType === 'renter' ? 'fromRenter' : 'fromOwner']);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Chargement de l'historique...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4, color: '#C9B6AB' }}>
        {title || 'Historique des locations'}
      </Typography>

      {rentals.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Aucune location trouvée dans votre historique.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {rentals.map((rental) => (
            <Grid item xs={12} md={6} lg={4} key={rental.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={rental.dressImage}
                  alt={rental.dressName}
                  sx={{ objectFit: 'cover' }}
                />
                
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', flex: 1 }}>
                      {rental.dressName}
                    </Typography>
                    {getStatusChip(rental.status)}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar 
                      src={userType === 'renter' ? rental.ownerAvatar : rental.renterAvatar} 
                      sx={{ width: 24, height: 24, mr: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {userType === 'renter' ? 'Propriétaire' : 'Locataire'}: {userType === 'renter' ? rental.ownerName : (rental.renterName || 'N/A')}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CalendarIcon sx={{ fontSize: 16, mr: 1, color: '#C9B6AB' }} />
                    <Typography variant="body2">
                      {new Date(rental.startDate).toLocaleDateString('fr-FR')} - {new Date(rental.endDate).toLocaleDateString('fr-FR')}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EuroIcon sx={{ fontSize: 16, mr: 1, color: '#C9B6AB' }} />
                    <Typography variant="h6" sx={{ color: '#C9B6AB', fontWeight: 'bold' }}>
                      {rental.totalPrice}€
                    </Typography>
                  </Box>

                  {/* Avis existants */}
                  {rental.reviews && (
                    <Box sx={{ mb: 2 }}>
                      {rental.reviews.fromRenter && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Votre avis:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Rating value={rental.reviews.fromRenter.rating} readOnly size="small" />
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {rental.reviews.fromRenter.comment}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      
                      {rental.reviews.fromOwner && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Avis du propriétaire:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Rating value={rental.reviews.fromOwner.rating} readOnly size="small" />
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {rental.reviews.fromOwner.comment}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  <Box sx={{ mt: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ViewIcon />}
                      onClick={() => setSelectedRental(rental)}
                      sx={{ 
                        borderColor: '#C9B6AB', 
                        color: '#C9B6AB',
                        '&:hover': { borderColor: '#C1A08E' }
                      }}
                    >
                      Détails
                    </Button>

                    {canReview(rental) && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<ReviewIcon />}
                        onClick={() => {
                          setSelectedRental(rental);
                          setReviewDialog(true);
                        }}
                        sx={{ 
                          backgroundColor: '#C9B6AB',
                          '&:hover': { backgroundColor: '#C1A08E' }
                        }}
                      >
                        Noter
                      </Button>
                    )}

                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ChatIcon />}
                      sx={{ 
                        borderColor: '#C9B6AB', 
                        color: '#C9B6AB',
                        '&:hover': { borderColor: '#C1A08E' }
                      }}
                    >
                      Chat
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog de détails */}
      <Dialog
        open={!!selectedRental && !reviewDialog}
        onClose={() => setSelectedRental(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedRental && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <img 
                  src={selectedRental.dressImage} 
                  alt={selectedRental.dressName}
                  style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, marginRight: 16 }}
                />
                <Box>
                  <Typography variant="h6">{selectedRental.dressName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userType === 'renter' ? 'Propriétaire' : 'Locataire'}: {userType === 'renter' ? selectedRental.ownerName : (selectedRental.renterName || 'N/A')}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Période de location
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedRental.startDate).toLocaleDateString('fr-FR')} - {new Date(selectedRental.endDate).toLocaleDateString('fr-FR')}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Prix total
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#C9B6AB' }}>
                    {selectedRental.totalPrice}€
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Adresse de récupération
                  </Typography>
                  <Typography variant="body1">
                    {selectedRental.pickupAddress}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Adresse de retour
                  </Typography>
                  <Typography variant="body1">
                    {selectedRental.returnAddress}
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={() => setSelectedRental(null)}>Fermer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog d'avis */}
      <Dialog
        open={reviewDialog}
        onClose={() => setReviewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Noter cette location
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Note (obligatoire)
            </Typography>
            <Rating
              value={reviewData.rating}
              onChange={(event, newValue) => {
                setReviewData(prev => ({ ...prev, rating: newValue || 0 }));
              }}
              size="large"
            />
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Commentaire (optionnel)"
              value={reviewData.comment}
              onChange={(e) => setReviewData(prev => ({ ...prev, comment: e.target.value }))}
              sx={{ mt: 2 }}
              placeholder="Partagez votre expérience..."
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setReviewDialog(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleReviewSubmit}
            variant="contained"
            disabled={reviewData.rating === 0}
            sx={{ 
              backgroundColor: '#C9B6AB',
              '&:hover': { backgroundColor: '#C1A08E' }
            }}
          >
            Envoyer l'avis
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RentalHistory;
